"use client";

import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Image from "next/image";
import PhoneInputWithCountrySelect, {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import { v4 as uuidv4 } from "uuid";
import { E164Number } from "libphonenumber-js/core";
import { motion } from "framer-motion";
import {
  getTodayDate,
  handleCampaignID,
  insertDataForOfficevist,
  insertDataInNetEnquiries,
  slots,
} from "@/lib/utils";
import { QueryParams, WelcomeBack } from "@/types/forms";
import {
  getQueryParamsFromUrl,
  googleTagManager,
} from "@/components/common/utils";
import {
  ExecutiveEducationSubCategory,
  FinanceAndAccountingSubcategory,
  FormHeading,
  MilesHealthcareSubCategory,
  OtherFormCategory,
  PageName,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import { usePathname } from "next/navigation";
import {
  clearFromOneDataFromLocal,
  getFormOneDataFromLocal,
  parseStoredData,
} from "@/lib/edcrypt";
import { MilesAlert } from "@/lib/alerts";
import { DisableDates, publicHolidays } from "@/data/formDisableDates";
import Link from "next/link";
import useMilesOneDeepLink from "@/hooks/useMilesOneDeepLink";
import saveAs from "file-saver";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDownIcon, CircleCheck } from "lucide-react";
import {
  useEvent,
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";
import { getWebinarEntry, getOrdinalDateParts } from "@/data/webinarSchedule";
import { captureAndStoreQueryParamsBase64 } from "@/lib/queryParams";

interface Vertical {
  Accouting: "accounting";
  Business: "business";
  Healthcare: "healthcare";
}

// Custom component to display country code instead of flag
const CountryCodeDisplay = ({ country }: { country: string }) => {
  const callingCode = country ? getCountryCallingCode(country as any) : "";
  return (
    <span className="inline-block min-w-[30px] text-sm font-medium">
      +{callingCode}
    </span>
  );
};

const GlobalForm: React.FC<{
  course?: string;
  courseId?: number;
  formfieldId?: string;
  formHeading?: string;
  formType: string;
  isAStepperForm?: boolean;
  pageType?: string;
  showSVG?: boolean;
  subCategory?: string;
  submitButtonName: string;
  successMessage: SuccessMessageType;
  verticalName?: VerticalCategory;
  darkBackground: boolean;
  brochureDownload?: boolean;
  formHeadingRight?: boolean;
  pageName?: string;
  defaultCityLocation?: string;
  webinarId?: string;
  isMilesSection?: boolean;
  isCairaSection?: boolean;
  isWebinarLandingPage?: boolean;
}> = ({
  course,
  courseId,
  formfieldId,
  formHeading,
  formType,
  isAStepperForm = false,
  pageType,
  showSVG,
  subCategory,
  submitButtonName,
  successMessage,
  verticalName,
  darkBackground = false,
  brochureDownload = false,
  formHeadingRight = false,
  pageName,
  defaultCityLocation,
  webinarId,
  isMilesSection,
  isWebinarLandingPage,
}) => {
  const __welcomeBack: WelcomeBack = {
    status: false,
    isDifferent: false,
  };

  const initialState = {
    connectWhatsApp: true,
    course: courseId !== null ? courseId : "",
    date: "",
    email: "",
    highest_education: "",
    name: "",
    nationality: "",
    phone: "",
    // qualification: "", // for graduation
    slot: "",
    vertical: "", // for contact us and sticky form
    // workInUS: "",
  };

  const btnInitalData = {
    text: submitButtonName ? submitButtonName : "Submit",
    disabled: false,
    show: true,
  };

  const [formState, setFormState] = useState({
    message: "",
    error: "",
  });

  const todayDate = getTodayDate();
  const [welcomeBack, setWelcomeBack] = useState<WelcomeBack>(__welcomeBack);

  const path = usePathname();
  const formRef = useRef<HTMLFormElement>(null);
  const officeVisitFormRef = useRef<HTMLFormElement>(null);

  const latestEvent = useEvent(pageName);
  const formattedDate = latestEvent?.formattedDate
    ? latestEvent.formattedDate.split(" ").slice(0, 2).join(" ")
    : "";
  const time = latestEvent?.time ? latestEvent?.time : "2:00 PM";
  const day = latestEvent?.day ? latestEvent?.day : "Saturday";

  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const weeklyDateParts = getOrdinalDateParts(weeklyWebinarEntry.date);

  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(initialState);
  const [formBtnText, setFormBtnText] = useState(btnInitalData);
  const [tkId, setTkId] = useState<string | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Date | undefined>(undefined);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { tryOpenApp } = useMilesOneDeepLink();
  const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF";

  const validateField = (
    value: any,
    errorMessage: string,
    type: string,
  ): boolean => {
    const validRegex = (email: string) => {
      if (email.endsWith("@mileseducation.com")) {
        return /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
      }
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    };

    let isValid = true;

    switch (type) {
      case "empty":
        isValid = !(value === null || value === undefined || value === "");
        break;
      case "empty_course":
        isValid = !(
          value === null ||
          value === undefined ||
          value === "" ||
          value === 0
        );
        break;
      case "email":
        isValid = value ? validRegex(value) : false;
        break;
      case "phoneLength":
        if (value) {
          isValid = isPossiblePhoneNumber(value) && isValidPhoneNumber(value);
        } else {
          isValid = false;
        }
        break;
      case "dayCheck":
        if (value) {
          const selectedDate = new Date(value);
          const isMonday = selectedDate.getDay() === 1;
          const isFirstOfMonth = selectedDate.getDate() === 1;
          isValid = !(isMonday || isFirstOfMonth);
        }
        break;
    }
    return isValid;
  };

  const getSuccessMessage = (successMessage: SuccessMessageType) => {
    switch (successMessage) {
      case SuccessMessageType.QUICK_CONNECT:
      case SuccessMessageType.CONTACT_US:
      case SuccessMessageType.BLOG_PAGE_FORM:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3
              className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-xl font-bold text-center mb-2`}
            >
              Thank you for your ​i​nquiry.
            </h3>
            <h2
              className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-xl font-normal text-center mb-4`}
            >
              Our programme advisor will get back to you shortly!
            </h2>
          </>
        );
      case SuccessMessageType.SOBA_BUSINESS_AND_LIBERALS:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3 className="text-2xl font-bold text-white text-center mb-2">
              Join Miles SOBA today!
            </h3>
            <h2 className="text-2xl font-semibold text-center mb-4 text-white">
              Thank you for your interest in School of Business and Liberal
              Arts(SOBA)
            </h2>
            <p className="text-center text-white">
              The brochure has been sent to your email. Please check your inbox
              (and spam folder, if needed). If you have any questions, feel free
              to reach out!
            </p>
          </>
        );
      case SuccessMessageType.MBA_ABROAD:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3 className="text-2xl font-bold text-white text-center mb-2">
              Join Miles MBA Abroad Today!
            </h3>
            <h2 className="text-2xl font-semibold md:font-medium text-center mb-4 text-white">
              Thank you for your interest in Miles MBA Abroad
            </h2>
            <p className="text-center text-white">
              The brochure has been sent to your email. Please check your inbox
              (and spam folder, if needed). If you have any questions, feel free
              to reach out!
            </p>
          </>
        );
      case SuccessMessageType.IIMI_BA:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3 className="text-2xl font-bold text-white text-center mb-2">
              IIM Indore - PG Certification in Branding & Advertising
            </h3>
            <h2 className="text-2xl font-semibold md:font-medium text-center mb-4 text-white">
              Thank you for your interest in the Investment Banking Program.
            </h2>
            <p className="text-center text-white">
              The brochure has been sent to your email. Please check your inbox
              (and spam folder, if needed). If you have any questions, feel free
              to reach out!
            </p>
          </>
        );
      case SuccessMessageType.ACCOUNTING:
        return (
          <>
            <div className="hidden md:block">
              <div className="w-full pb-4 flex justify-center">
                <CircleCheck className="w-10 h-10 text-blue-500" />
              </div>

              <p
                className={`rounded-md md:text-lg font-semibold text-center ${darkBackground ? "text-white" : "text-[#3399FF]"}`}
              >
                {/* Your online meet is confirmed! */}
                Thank you! Your details have been received.
              </p>
              <div className="text-center">
                {!isMilesSection && (
                  <p
                    className={`px-4 font-medium ${darkBackground ? "text-white" : "text-[#212953]"}`}
                  >
                    You're all set to connect with our expert on {data.date},{" "}
                    {data.slot}.
                  </p>
                )}
                <p
                  className={`font-semibold text-base mt-2 ${darkBackground ? "text-white" : "text-[#212953]"}`}
                >
                  {/* You'll shortly receive an email with a Google Meet link and
                  session details. */}
                  One of our academic experts
                  <span style={{ display: "block" }}>
                    will reach out to you shortly.
                  </span>
                </p>
              </div>
              <div className="bg-[linear-gradient(94deg,_#061A2F_0.32%,_#135295_99.92%)] p-5 flex rounded-xl items-center justify-between mt-4">
                <div className="flex flex-col">
                  <Image
                    src="/miles_logo_for_form.png"
                    alt="App Store"
                    width={30}
                    height={30}
                    className="cursor-pointer"
                  />
                  <p className="font-semibold text-white w-fit mt-2 leading-[100%] text-sm">
                    Download the <br />
                    Miles One App
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <Link
                    href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
                    target="_blank"
                    className="flex items-center gap-2 px-2 py-2 rounded-lg bg-[#fff] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)] w-[120px]"
                  >
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp"
                      alt="App Store"
                      width={24}
                      height={24}
                      className="cursor-pointer"
                    />
                    <div className="text-black text-left leading-tight">
                      <p className="text-[10px]">Download on the</p>
                      <p className="text-[12px] font-medium">App Store</p>
                    </div>
                  </Link>

                  <Link
                    href="https://play.google.com/store/apps/details?id=com.miles.one"
                    target="_blank"
                    className="flex items-center gap-2 px-2 py-2 rounded-lg bg-[#fff] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)] w-[120px]"
                  >
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp"
                      alt="Play Store"
                      width={30}
                      height={30}
                      className="cursor-pointer"
                    />
                    <div className="text-black text-left leading-tight">
                      <p className="text-[10px]">GET IT ON</p>
                      <p className="text-[12px] font-medium">Play Store</p>
                    </div>
                  </Link>
                </div>
                <div className="flex flex-col items-center justify-center mt-4 cursor-pointer">
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
                    alt="QR Code"
                    width={100}
                    height={100}
                    className=" bg-white border-[1px] border-black"
                  />
                  <p className="text-[10px] text-[#EEEEEE]">Scan to download</p>
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <div className="w-full pb-4 flex justify-center">
                <CircleCheck className="w-10 h-10 text-blue-500" />
              </div>

              <p
                className={`rounded-md font-semibold text-center ${darkBackground ? "text-white" : "text-[#3399FF]"}`}
              >
                {/* Your online meet is confirmed! */}
                Thank you! Your details have been received.
              </p>
              <div className="text-center">
                <p
                  className={`font-semibold mt-2 text-sm ${darkBackground ? "text-white" : "text-[#212953]"}`}
                >
                  {/* You'll shortly receive an email with a Google Meet link and
                    session details. */}
                  One of our academic experts
                  <span style={{ display: "block" }}>
                    will reach out to you shortly.
                  </span>
                </p>
              </div>
              <div className="mt-4 bg-[linear-gradient(94deg,_#061A2F_0.32%,_#135295_99.92%)] rounded-xl p-3">
                <div className="flex gap-3 items-center">
                  <Image
                    src="/miles_logo_for_form.png"
                    alt="App Store"
                    width={24}
                    height={14}
                    className="cursor-pointer h-4"
                  />
                  <p className="text-base font-semibold text-white">
                    Download the MilesOne App now!
                  </p>
                </div>
                <button
                  className="w-full px-1 py-2 justify-center rounded-[12px_12px_12px_0px] mt-3 flex bg-white text-[#316BC9] font-semibold"
                  onClick={() => {
                    tryOpenApp();
                  }}
                >
                  Open
                </button>
              </div>
            </div>
          </>
        );
      case SuccessMessageType.LIVE_WEBINAR:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3
              className={`text-2xl font-bold ${darkBackground ? "text-white" : "text-[#212953]"} text-center mb-2`}
            >
              Registration Successful!
            </h3>
            <h2
              className={`text-lg md:text-xl font-normal text-center mb-4 ${darkBackground ? "text-white" : "text-[#212953]"}`}
            >
              Thank you for registering. We have received your details and will
              get back to you shortly with more information.
            </h2>
          </>
        );
      case SuccessMessageType.CAIRA:
        return (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto mb-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h3
              className={`text-2xl font-bold ${darkBackground ? "text-white" : "text-[#212953]"} text-center mb-2`}
            >
              Registration Successful!
            </h3>
            <h2
              className={`text-lg md:text-xl font-normal text-center mb-4 ${darkBackground ? "text-white" : "text-[#212953]"}`}
            >
              Thank you for registering. We have received your details and will
              get back to you shortly with more information.
            </h2>
            <div className="flex justify-center">
              <Link
                href="/caira"
                className="mx-auto mt-4 w-fit text-[#3399FF] underline"
              >
                Visit Caira Website
              </Link>
            </div>
          </>
        );
      case SuccessMessageType.DOWNLOAD_BROCHURE:
        return (
          <>
            <div className="pb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto mb-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
                />
              </svg>

              <h2 className="text-3xl font-bold text-center mb-4 text-white">
                Thank you!
              </h2>
              <p className="text-center font-medium text-white text-base md:text-lg">
                The brochure has now begun downloading to your device and can be
                accessed within the downloads folder. If you have any further
                questions, feel free to reach out!
              </p>
            </div>
          </>
        );
      case SuccessMessageType.MBA_ONLINE_AI:
        return (
          <>
            <div className="p-6 rounded-2xl">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto mb-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
                />
              </svg>
              <div>
                <h2 className="text-2xl font-bold text-center mb-4 text-white">
                  Thank you for reaching out!
                </h2>
                <p className="text-center text-white">
                  Our representative will get in touch with you shortly.
                </p>
              </div>
            </div>
          </>
        );
      default:
        return <></>;
    }
  };

  const connectMetoDifferentAcount = () => {
    clearFromOneDataFromLocal(LOCAL_STORE_NAME);
    setData(initialState);
    setCurrentStep(1);
    setFormBtnText(btnInitalData);
    setFormState({ message: "", error: "" });
  };

  useEffect(() => {
    const init = async () => {
      try {
        const campaignId = handleCampaignID();
        setTkId(campaignId);
      } catch (error: any) {
        console.error(`Error fetching event data: ${error.message}`);
      }
    };

    init();
  }, []);

  const registerForWebinar = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setErrors({});
    const newErrors: { [key: string]: string } = {};

    if (!validateField(data.date, "Please select date", "empty")) {
      newErrors.date = "Please select date";
    }
    if (
      !validateField(
        data.date,
        "Please select any day other than Monday and 1st of month",
        "dayCheck",
      )
    ) {
      newErrors.date =
        "Please select any day other than Monday and 1st of month";
    }
    if (!validateField(data.slot, "Please select time", "empty")) {
      newErrors.slot = "Please select time";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    btnLoading();
    try {
      const encrypted = localStorage.getItem("MILES_NEXT_F1_REF");

      let decrypted = null;

      if (encrypted) {
        try {
          decrypted = parseStoredData(encrypted);
        } catch (error) {
          console.error(error);
          return;
        }
      }

      const date = data?.date.trim();
      const slot = data?.slot.trim();

      const convertTo24HoursFormat = (time: string): string => {
        const [hourMinute, period] = time.split(" ");
        let [hours, minutes] = hourMinute.split(":").map(Number);

        if (period === "PM" && hours !== 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }

        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
      };

      const time24 = convertTo24HoursFormat(slot);
      const dateTimeString = `${date}T${time24}+05:30`;
      const dateTime = new Date(dateTimeString);

      if (isNaN(dateTime.getTime())) {
        throw new Error("Invalid date-time format");
      }

      const payload = {
        date_time: dateTime.toISOString().split(".")[0] + "Z",
        miles_uuid: decrypted?.miles_uuid || null,
        title: "Miles Expert Connect",
      };

      const { data: resultData, error } =
        await insertDataForOfficevist(payload);

      const isSuccess = !error && resultData?.statusCode === 200;

      if (isSuccess) {
        setFormBtnText(btnInitalData);
        clearFromOneDataFromLocal(LOCAL_STORE_NAME);
        setWelcomeBack(__welcomeBack);
        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
          disabled: false,
        }));
        setCurrentStep(3);
        googleTagManager("gtm_form2_filled", {
          ...data,
          user_track_id: localStorage.getItem("track")
            ? localStorage.getItem("track")
            : null,
          coming_from: `website-${formType}`,
        });
        localStorage.setItem("caira_form_filled", "true");
      } else {
        console.error(error);
      }
    } catch (error) {
      console.error(error);
      setFormBtnText(btnInitalData);
    }
  };

  const handlePhoneChange = (value?: E164Number | undefined) => {
    const phoneValue = value ?? "";
    setData((prevData) => ({
      ...prevData,
      phone: phoneValue,
    }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleChange = (
    e: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | Vertical[keyof Vertical]
    >,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement)?.checked : value;

    setData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const btnLoading = () => {
    setFormBtnText((prev) => ({
      ...prev,
      text: "Submitting...",
      disabled: true,
    }));
  };

  const submitFormData = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isWebinarLandingPage) {
      googleTagManager("Webinar_LP_RegisterForWebinar", {
        button_location: "WebinarBanner",
        page_url: window.location.href,
      });
    }
    try {
      const obj: QueryParams = localStorage?.getItem("seo_params")
        ? JSON.parse(localStorage.getItem("seo_params") ?? "")
        : { gclid: null, tk: null };

      setErrors({});
      const newErrors: { [key: string]: string } = {};

      if (!validateField(data.name, "Please enter your full name", "empty")) {
        newErrors.name = "Please enter your full name";
      }
      if (!validateField(data.phone, "Please enter mobile number", "empty")) {
        newErrors.phone = "Please enter mobile number";
      } else if (
        !validateField(
          data.phone,
          "Please enter valid mobile number",
          "phoneLength",
        )
      ) {
        newErrors.phone = "Please enter valid mobile number";
      }
      if (!validateField(data.email, "Please enter email", "empty")) {
        newErrors.email = "Please enter email";
      }
      if (!validateField(data.email, "Please enter valid email", "email")) {
        newErrors.email = "Please enter valid email";
      }

      if (
        verticalName === VerticalCategory.MILES_HEALTHCARE &&
        subCategory === MilesHealthcareSubCategory.NURSES
      ) {
        if (
          !validateField(
            data.nationality,
            "Please enter your nationality",
            "empty",
          )
        ) {
          newErrors.nationality = "Please enter your nationality";
        }

        if (
          !validateField(
            data.highest_education,
            "Please select your highest education",
            "empty",
          )
        ) {
          newErrors.highest_education = "Please select your highest education";
        }
      }

      const courseProgramId = data.course ? Number(data.course) : null;
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      btnLoading();

      const isHealthcareNurse =
        verticalName === VerticalCategory.MILES_HEALTHCARE &&
        subCategory === MilesHealthcareSubCategory.NURSES;

      const payload = {
        first_name: data.name,
        phone: data.phone || null,
        email: data.email || null,
        city: defaultCityLocation || "",
        nationality: isHealthcareNurse ? data.nationality || "" : "",
        highestEducation: isHealthcareNurse ? data.highest_education || "" : "",
        whatsapp_opt_in: Boolean(data.connectWhatsApp),
        campaign_tk: tkId || "e3a268cf-420c-415c-a234-2825d878e688",
        whatsappAllow: 1,
        smsAllow: 1,
        gcl_id: obj?.gclid || null,
        course: data.course || null,
        course_id:
          verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING ||
          verticalName === VerticalCategory.EXECUTIVE_EDUCATION
            ? courseId
            : courseProgramId,
        interested_webinar_id: webinarId || null,
        register_for_webinar: true,
        coming_from: `website-${formType}`,
        page_full_url: window.location.href,
        url_page_path: path,
        user_track_id: localStorage.getItem("track") || null,
        query_params_base64: captureAndStoreQueryParamsBase64() ?? "",
      };

      const { data: resultData, error } =
        await insertDataInNetEnquiries(payload);
      const enquiryBody = resultData?.body;
      const isSuccess = !error && resultData?.statusCode === 201 && enquiryBody;

      if (isSuccess) {
        setData(initialState);
        setFormBtnText(btnInitalData);
        isMilesSection && setCurrentStep(3);
        if (isMilesSection) {
          localStorage.setItem(
            "MILES_NEXT_F1_REF",
            JSON.stringify({
              ...data,
              savedDate: new Date(),
              id: enquiryBody.id,
              url: window.location.href,
              uid: enquiryBody.uuid,
              miles_uuid: enquiryBody.miles_uuid,
              isMilesSection: true,
            }),
          );
        } else {
          localStorage.setItem(
            "MILES_NEXT_F1_REF",
            JSON.stringify({
              ...data,
              savedDate: new Date(),
              id: enquiryBody.id,
              url: window.location.href,
              uid: enquiryBody.uuid,
              miles_uuid: enquiryBody.miles_uuid,
              isMilesSection: false,
            }),
          );
        }

        sessionStorage.setItem("formSubmitted", "true");
        localStorage.setItem("formSubmitted", "true");

        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
          disabled: false,
        }));
        setFormState({ message: "Success", error: "" });

        // Handle brochure downloads
        if (brochureDownload) {
          const pdfUrls = {
            [PageName.CPA]: "/pdf/CPA_Roadmap.pdf",
            [PageName.CMA]: "/pdf/CMA_Roadmap.pdf",
            [PageName.MBA_AI]: "/pdf/MBAOnline-ai.pdf",
          };

          const pdfUrl = pdfUrls[pageName as keyof typeof pdfUrls];
          if (pdfUrl && pageName) {
            const fileName = `${pageName.replace(/_/g, "")}_Roadmap.pdf`;
            saveAs(pdfUrl, fileName);
          }
        }

        // Navigate to next step
        if (!formState.error) {
          isAStepperForm
            ? //  setCurrentStep(2)
              setCurrentStep(3)
            : setCurrentStep(3);
        }

        // Track form submission
        googleTagManager("gtm_form1_filled", {
          ...data,
          courseTitle: payload.course,
          course: payload.course_id,
          whatsappAllow: payload.whatsappAllow,
          smsAllow: payload.smsAllow,
          user_track_id: payload.user_track_id,
          coming_from: payload.coming_from,
        });
      } else {
        // Handle submission error
        const errorMessage = error?.message || "Something went wrong";

        MilesAlert({
          text: errorMessage,
          type: "error",
        }).then(() => {
          setFormBtnText(btnInitalData);
        });

        setFormState({
          message: "Error",
          error: "An unexpected error occurred while adding data",
        });
      }
    } catch (error) {
      if (error) {
        setFormState({
          message: "Error",
          error: "error",
        });
        setFormBtnText(btnInitalData);
      } else {
        setFormState({ message: "", error: "An unexpected error occurred" });
      }
    }
  };

  const formData = getFormOneDataFromLocal(LOCAL_STORE_NAME);

  useEffect(() => {
    getQueryParamsFromUrl();
    (() => {
      const localData = getFormOneDataFromLocal(LOCAL_STORE_NAME);
      if (localData.status) {
        setData((prev) => ({
          ...prev,
          name: localData.data.name,
          phone: localData.data.phone,
          email: localData.data.email,
          whatsappAllow: localData.data.whatsappAllow,
          smsAllow: localData.data.smsAllow,
          intrestedin: localData.data.intrestedin,
          courseTitle: localData.data.courseTitle,
          eduDetails: localData.data.eduDetails,
        }));
        isAStepperForm &&
          !isMilesSection &&
          !localData.data.isMilesSection &&
          // setCurrentStep(2);
          setCurrentStep(3);
        isAStepperForm && isMilesSection && setCurrentStep(3);

        setWelcomeBack((prev) => ({
          ...prev,
          status: true,
          isDifferent: localData.data.course === courseId ? false : true,
        }));
        setFormBtnText((prev) => ({
          ...prev,
          text: submitButtonName,
        }));
      }
    })();
  }, []);

  useEffect(() => {
    setData(initialState);
    setFormBtnText(btnInitalData);
    captureAndStoreQueryParamsBase64();
  }, []);

  return (
    <div>
      {/* Step 1 */}
      {currentStep === 1 &&
        formState?.message === "" &&
        formState?.error === "" && (
          <div>
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              subCategory === FinanceAndAccountingSubcategory.USP &&
              formHeading ===
                FormHeading.APPLY_FOR_THE_MILES_US_PATHWAY_TO_LIVE_THE_AMERICAN_DREAM && (
                <>
                  <h3 className="text-white md:text-xl text-center font-medium pb-1">
                    Apply for the Miles US Pathway to live
                  </h3>
                  <h2 className="text-2xl text-[#ff9d39] font-bold text-center pb-4">
                    The American Dream
                  </h2>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              subCategory === FinanceAndAccountingSubcategory.USP &&
              formHeading === FormHeading.LIMITED_SEATS_HURRY && (
                <>
                  <h3 className="text-white md:text-xl text-center font-medium pb-4">
                    Limited Seats!{" "}
                    <span className="text-[#ff9d39]">Hurry!</span>
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              (subCategory === FinanceAndAccountingSubcategory.CPA ||
                subCategory === FinanceAndAccountingSubcategory.CMA ||
                subCategory === FinanceAndAccountingSubcategory.USP) &&
              formHeading ===
                FormHeading.JOIN_US_THIS_SUNDAY_AT_4_PM_FOR_A_LIVE_WEBINAR && (
                <>
                  <h3 className="text-white md:text-xl text-center font-semibold pb-4">
                    Join us this <span className="text-[#FFBA08]">Sunday </span>{" "}
                    at <span className="text-[#FFBA08]">4:00 PM</span> for a
                    live webinar!
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              (subCategory === FinanceAndAccountingSubcategory.CPA ||
                subCategory === FinanceAndAccountingSubcategory.CMA ||
                subCategory === FinanceAndAccountingSubcategory.USP ||
                subCategory === FinanceAndAccountingSubcategory.EA) &&
              formHeading ===
                FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE && (
                <>
                  <h3 className="text-white md:text-xl text-center font-semibold pb-4 px-8 md:px-2">
                    Schedule a visit to{" "}
                    <span className="text-[#FFBA08]">Miles office </span> or
                    meet us online
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              (subCategory === FinanceAndAccountingSubcategory.CPA ||
                subCategory === FinanceAndAccountingSubcategory.CMA ||
                subCategory === FinanceAndAccountingSubcategory.USP ||
                subCategory === FinanceAndAccountingSubcategory.EA) &&
              formHeading ===
                FormHeading.RECEIVE_PROGRAM_BROCHURE_VIA_EMAIL && (
                <>
                  <h3
                    className={`${darkBackground ? "text-white" : "text-[#212953]"} text-base md:text-lg text-center font-semibold pb-4`}
                  >
                    Receive program brochure via email
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              subCategory === FinanceAndAccountingSubcategory.USP &&
              formHeading === FormHeading.FALL_2025_BATCH_CLOSING_SOON && (
                <>
                  <h3 className="text-white md:text-xl text-center font-medium pb-4">
                    Fall 2025 Batch{" "}
                    <span className="text-[#ff9d39]">Closing Soon</span>
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.FINANCE_AND_ACCOUNTING &&
              (subCategory === FinanceAndAccountingSubcategory.CPA ||
                subCategory === FinanceAndAccountingSubcategory.CMA ||
                subCategory === FinanceAndAccountingSubcategory.USP) &&
              formHeading === FormHeading.WORLD_FAVORITE_CMA_CPA_INSTRUCTOR && (
                <>
                  <h3
                    className={`${darkBackground ? "text-white" : "text-black"} md:text-base text-center font-semibold pb-4 md:leading-[22px]`}
                  >
                    Live Webinar: AI-Ready {course}
                    <br />
                    By Varun Jain
                    <br />
                    {isScheduleLoading ? (
                      <WebinarDateSkeleton width={150} />
                    ) : (
                      <>
                        <motion.span
                          initial={{ opacity: 0, y: 10 }}
                          animate={{
                            opacity: formattedDate && day ? 1 : 0,
                            y: formattedDate && day ? 0 : 10,
                          }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="text-[#3399FF] text-sm font-semibold"
                        >
                          On {formattedDate}, {day} at
                        </motion.span>{" "}
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{
                            opacity: time ? 1 : 0,
                            scale: time ? 1 : 0.9,
                          }}
                          transition={{
                            duration: 0.1,
                            delay: 0.8,
                            ease: "easeOut",
                          }}
                          className="text-[#3399FF] text-sm font-semibold"
                        >
                          {time}
                        </motion.span>
                      </>
                    )}
                  </h3>
                </>
              )}
            {verticalName === VerticalCategory.EXECUTIVE_EDUCATION &&
              subCategory === ExecutiveEducationSubCategory.SoBA &&
              formHeading === FormHeading.JOIN_MILES_SOBA_TODAY && (
                <h3 className="text-white md:text-2xl text-center font-medium pb-4">
                  Join Miles SOBA today!
                </h3>
              )}
            {verticalName === VerticalCategory.EXECUTIVE_EDUCATION &&
              subCategory === ExecutiveEducationSubCategory.MBA_ABROAD &&
              formHeading === FormHeading.JOIN_MILES_MBA_ABROAD_TODAY && (
                <h3
                  className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-center font-medium pb-4`}
                >
                  Join Miles MBA Abroad Today!
                </h3>
              )}
            {verticalName === VerticalCategory.EXECUTIVE_EDUCATION &&
              subCategory === ExecutiveEducationSubCategory.MBA_ABROAD &&
              formHeading === FormHeading.JOIN_MILES_GLOBAL_MBA_TODAY && (
                <h3 className="text-white md:text-2xl text-center font-medium pb-4">
                  Join the Miles Global MBA Today!
                </h3>
              )}
            {verticalName === VerticalCategory.EXECUTIVE_EDUCATION &&
              subCategory === ExecutiveEducationSubCategory.IIMI_BA &&
              formHeading ===
                FormHeading.IIM_INDORE_PG_CERTIFICATION_IN_BRANDING_AND_ADVERTISING && (
                <h3 className="text-white md:text-2xl text-center font-medium pb-4">
                  IIM Indore - PG Certification in Branding & Advertising
                </h3>
              )}
            {verticalName === VerticalCategory.EXECUTIVE_EDUCATION &&
              subCategory === ExecutiveEducationSubCategory.MBA_ONLINE_AI &&
              formHeading === FormHeading.MBA_ONLINE_AI && (
                <h3 className="text-white md:text-xl text-center font-medium pb-4">
                  Webinar on MBA in Artificial Intelligence
                </h3>
              )}
            {pageType === OtherFormCategory.CONTACT_US && (
              <h2
                className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-center font-semibold pb-4`}
              >
                Connect With Us
              </h2>
            )}
            {pageType === OtherFormCategory.QUICK_CONNECT && (
              <h2
                className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-xl text-center font-semibold pb-4`}
              >
                Connect With Us
              </h2>
            )}
            {pageType === OtherFormCategory.BLOG_PAGE_FORM && (
              <h2
                className={`${darkBackground ? "text-white" : "text-[#2A85FF]"} md:text-2xl text-center font-semibold pb-4 tracking-[-0.4px]`}
              >
                Get in touch with Us!
              </h2>
            )}

            {formHeading ===
              FormHeading.JOIN_THE_WEBINAR_AND_MAKE_THE_BIG_DECISION && (
              <h2
                className={`md:text-xl text-center font-semibold pb-4 ${darkBackground ? "text-white" : "text-[#212953]"}`}
              >
                Join the webinar & make the <br />
                <span className="text-[#ff9d39] font-semibold">
                  BIG decision!
                </span>
              </h2>
            )}

            {formHeading === FormHeading.CPA_ACCOUNTING && (
              <h2
                className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-2xl text-center font-semibold pb-4`}
              >
                Connect With Us!
              </h2>
            )}

            {formHeading ===
              FormHeading.CONNECT_WITH_A_CPA_EXPERT_FOR_DETAILS && (
              <>
                <h2 className="text-[28px] md:text-[32px] font-bold text-[#FFBA08] text-left mb-4 leading-[1.1]">
                  Want to be an AI-Ready <br className="hidden md:block" />
                  Accountant ?
                </h2>
                <h2
                  className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-base text-left font-semibold pb-4`}
                >
                  Connect with an Expert
                </h2>
              </>
            )}

            {formHeading ===
              FormHeading.CONNECT_WITH_A_CMA_EXPERT_FOR_DETAILS && (
              <>
                <h2 className="text-[#39F] text-[20px] font-bold leading-[1.2] tracking-[-0.02em] mb-2">
                  Want to be an AI-Ready
                  <br className="block md:hidden" /> Accountant?
                </h2>
                <p className="text-[#333] text-[14px] font-normal leading-normal mb-4">
                  Connect with an Expert
                </p>
              </>
            )}

            {formHeading ===
              FormHeading.CONNECT_WITH_A_CPA_CMA_EXPERT_FOR_DETAILS && (
              <>
                <h2 className="text-[28px] md:text-[32px] font-bold text-[#FFBA08] text-left mb-4 leading-[1.1]">
                  Want to be an AI-Ready <br className="hidden md:block" />
                  Accountant ?
                </h2>
                <h2
                  className={`${darkBackground ? "text-white" : "text-[#212953]"} md:text-base text-left font-semibold pb-4`}
                >
                  Connect with an Expert
                </h2>
              </>
            )}

            {formHeading === FormHeading.JOIN_US_LIVE_WITH_VARUN_JAIN &&
              webinarId !== "149" && (
                <div className="text-center pb-4">
                  <h2
                    className={`md:text-base text-sm font-semibold md:leading-5 ${darkBackground ? "text-white" : "text-black"}`}
                  >
                    Live Webinar: {course} + AI-readiness
                    <br />
                    {!isWebinarLandingPage ? "By Varun Jain" : ""}
                  </h2>
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                          opacity: formattedDate && day ? 1 : 0,
                          y: formattedDate && day ? 0 : 10,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="text-[#3399FF] text-sm font-semibold"
                      >
                        On {formattedDate}, {day} at
                      </motion.span>{" "}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: time ? 1 : 0,
                          scale: time ? 1 : 0.9,
                        }}
                        transition={{
                          duration: 0.1,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                        className="text-[#3399FF] text-sm font-semibold"
                      >
                        {time}
                      </motion.span>
                    </>
                  )}
                </div>
              )}
            {formHeading === FormHeading.JOIN_US_LIVE_WITH_VARUN_JAIN &&
              webinarId === "149" && (
                <div className="text-center pb-4">
                  <h2
                    className={`md:text-base text-sm font-semibold md:leading-5 ${darkBackground ? "text-white" : "text-black"}`}
                  >
                    Register for AI-Ready {course} Webinar <br /> By Varun Jain
                  </h2>
                </div>
              )}

            {formHeading === FormHeading.KICKSTART_YOUR_CPA_CAREER_HERE && (
              <p
                className={`text-white text-base md:text-xl font-semibold ${formHeadingRight ? "text-right" : "text-center"} pb-4 leading-[1.2]`}
              >
                Kickstart Your CPA{" "}
                <br className={`${formHeadingRight ? "block" : "hidden"}`} />
                Career here
              </p>
            )}
            {formHeading === FormHeading.KICKSTART_YOUR_CMA_CAREER_HERE && (
              <p
                className={`text-white text-base md:text-xl font-semibold ${formHeadingRight ? "text-right" : "text-center"} pb-4 leading-[1.2]`}
              >
                Kickstart Your CMA{" "}
                <br className={`${formHeadingRight ? "block" : "hidden"}`} />
                Career here
              </p>
            )}
            {formHeading ===
              FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN && (
              <h3 className="text-lg font-semibold text-white text-center mb-4">
                Register for the AI in Accounting 101 Webinar
              </h3>
            )}
            {formHeading ===
              FormHeading.LIVE_AI_IN_ACCOUNTING_101_WEBINAR_POPUP && (
              <>
                <h2 className="text-white text-[18px] md:text-[16px] font-bold leading-tight tracking-[-0.01em] text-center mb-1">
                  AI in Accounting 101 Webinar
                </h2>
                <p className="text-white text-[15px] md:text-[12px] font-semibold text-center mb-4">
                  <span className="text-[#FF3B30] mr-1">●</span>
                  <span className="font-bold text-[#FF3B30]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                          opacity: formattedDate && day ? 1 : 0,
                          y: formattedDate && day ? 0 : 10,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="font-bold"
                      >
                        {formattedDate}, {day} at
                      </motion.span>{" "}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: time ? 1 : 0,
                          scale: time ? 1 : 0.9,
                        }}
                        transition={{
                          duration: 0.1,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                        className="text-[#FFBA08] font-bold"
                      >
                        {time}
                      </motion.span>
                    </>
                  )}
                </p>
              </>
            )}
            {formHeading === FormHeading.MONTHLY_WEBINAR && (
              <>
                <h2 className="text-white text-[18px] md:text-[16px] font-bold leading-tight tracking-[-0.01em] text-center mb-1 md:px-0 px-4">
                  {latestEvent?.title?.replace(/-/g, " ")}
                </h2>
                <p className="text-white text-[15px] md:text-[12px] font-semibold text-center mb-4">
                  <span className="text-[#FF3B30] mr-1">●</span>
                  <span className="font-bold text-[#FF3B30]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={150} />
                  ) : (
                    <>
                      <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{
                          opacity: formattedDate && day ? 1 : 0,
                          y: formattedDate && day ? 0 : 10,
                        }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="font-bold"
                      >
                        {weeklyDateParts.day}
                        <sup>{weeklyDateParts.suffix}</sup>{" "}
                        {weeklyDateParts.month.slice(0, 3)},{" "}
                        {weeklyWebinarEntry.day} at
                      </motion.span>{" "}
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: time ? 1 : 0,
                          scale: time ? 1 : 0.9,
                        }}
                        transition={{
                          duration: 0.1,
                          delay: 0.8,
                          ease: "easeOut",
                        }}
                        className="text-[#FFBA08] font-bold"
                      >
                        04:00 PM
                      </motion.span>
                    </>
                  )}
                </p>
              </>
            )}
            {formHeading === FormHeading.GET_YOUR_CAIRA_ROADMAP && (
              <div className="mb-6 text-center">
                <h3 className="text-white text-xl md:text-2xl font-semibold mb-1">
                  Get your CAIRA Roadmap
                </h3>
                <p className="text-white/60 text-sm md:text-base">
                  and the right upgrade path with CPA or CMA.
                </p>
              </div>
            )}
            {formHeading === FormHeading.TALK_TO_CAIRA_AUDITORS && (
              <div className="mb-6 text-center">
                <h3 className="text-white text-xl md:text-2xl font-semibold mb-1">
                  Get your CAIRA Roadmap
                </h3>
                <p className="text-white/60 text-sm md:text-base">
                  And the right upgrade pathway alongside your CA.
                </p>
              </div>
            )}
            <form ref={formRef} onSubmit={submitFormData}>
              <div className="mb-2">
                <label htmlFor="form-fullname-field" className="sr-only">
                  Full Name
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  id="form-fullname-field"
                  name="name"
                  value={data.name}
                  placeholder="Full Name*"
                  onChange={handleChange}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.name ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="mb-2">
                <label htmlFor="phone" className="sr-only">
                  Phone Number
                </label>
                <PhoneInputWithCountrySelect
                  id="phone"
                  name="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(value) =>
                    handlePhoneChange(value as E164Number | undefined)
                  }
                  placeholder="Phone Number*"
                  autoComplete="off"
                  defaultCountry="IN"
                  flagComponent={CountryCodeDisplay}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.phone ? "border-red-500 bg-white" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="mb-2">
                <label htmlFor="form-email-field" className="sr-only">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="off"
                  id="form-email-field"
                  value={data.email}
                  placeholder="E-mail*"
                  onChange={handleChange}
                  className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${errors.email ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-[1px] ml-1">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="mb-2 ml-1 flex items-center justify-start">
                <input
                  id="page"
                  name="connectWhatsApp"
                  type="checkbox"
                  checked={data.connectWhatsApp}
                  onChange={handleChange}
                  className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                  rounded-full mr-2 border-2 ${darkBackground ? "border-white" : "border-black"}
                  checked:bg-[#39f] checked:border-0
                  after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                  after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                  after:font-bold after:text-white after:opacity-0
                  checked:after:opacity-100 after:transition-opacity after:duration-200`}
                />

                <label
                  htmlFor="page"
                  className={` text-[8px] md:text-[9px] font-medium tracking-[-0.373px] cursor-pointer ${
                    darkBackground ? "text-[#D5D5D5]" : "text-[#002551]"
                  }`}
                >
                  I agree to receive updates via
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                    alt="Whatsapp logo"
                    width={12}
                    height={12}
                    className="mx-[2px] inline-block"
                  />
                  WhatsApp, Email & SMS.
                  <a
                    href="/privacy"
                    target="_blank"
                    className={`ml-1 underline ${
                      darkBackground ? "text-[#3399FF]" : "text-[#3399FF]"
                    }`}
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <div
                className={`${showSVG ? "flex gap-2 items-center justify-center group" : ""}`}
              >
                {showSVG && (
                  <Image
                    priority
                    src="/images/accounting/arrow.png"
                    alt="arrow image"
                    className="new_arrow md:w-12 w-10 md:h-12 h-10 transform transition-transform duration-300 group-hover:rotate-45"
                    width={48}
                    height={48}
                  />
                )}
                <button
                  className={`font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 ${showSVG ? "w-[70%]" : "w-full"} capitalize rounded-[24px_24px_24px_0px]`}
                  type="submit"
                  disabled={formBtnText.disabled}
                >
                  {formBtnText.text}
                </button>
              </div>
              {subCategory === ExecutiveEducationSubCategory.MBA_ONLINE_AI && (
                <p className="text-base text-white mt-5 text-center">
                  Happening on
                  <span className="text-[#FFBA08] font-bold">
                    {" "}
                    28th June 2025
                  </span>{" "}
                  at <span className="text-[#FFBA08] font-bold"> 4PM</span>
                </p>
              )}
            </form>
          </div>
        )}
      {formState?.error === "" && currentStep === 2 && (
        <form ref={officeVisitFormRef} onSubmit={registerForWebinar}>
          <div>
            {(!welcomeBack.isDifferent || welcomeBack.isDifferent) && (
              <>
                {formState?.message === "Success" ? (
                  <>
                    <h3
                      className={`font-bold text-center md:text-base text-base mb-1 ${darkBackground ? "text-white" : "text-[#212953]"}`}
                    >
                      Thanks, {formData.data.name}
                    </h3>
                    <p
                      className={`text-[#3399ff] font-semibold text-center md:text-[20px] text-base mb-1 md:mb-2 ${darkBackground ? "text-[##3399FF]" : "text-[#212953]"}`}
                    >
                      Book Your 1:1 Expert Session
                    </p>
                    <p
                      className={`md:text-xs text-xs font-medium text-center px-4 mb-2 md:mb-4 ${darkBackground ? "text-white" : "text-[#212953]"}`}
                    >
                      Get a personalised {pageName} roadmap with our in-house
                      expert
                    </p>
                  </>
                ) : (
                  <>
                    {welcomeBack.status && (
                      <>
                        <h2
                          className={`font-bold md:text-[24px] text-[20px] mb-1 md:leading-[30px] text-center ${darkBackground ? "text-white" : "text-[#212953]"}`}
                        >
                          Welcome back,{" "}
                          <span className="font-bold capitalize text-[#FFBA08]">
                            {formData.data.name}
                          </span>
                        </h2>
                        <p
                          className={`text-center md:text-[20px] text-base font-medium mb-1 md:mb-2 ${darkBackground ? "text-white" : "text-[#212953]"}`}
                        >
                          Book Your 1:1 Expert Session
                        </p>
                        <p
                          className={`md:text-base text-sm font-medium text-center px-4 md:px-2 mb-2 md:mb-4 ${darkBackground ? "text-white" : "text-[#212953]"}`}
                        >
                          Get a personalised CPA roadmap with our in-house
                          expert
                        </p>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <div className="mb-2">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="date"
                    className={`w-full px-4 pr-0.5 py-3 h-[48px] justify-between !text-sm md:text-base rounded-xl font-medium ${errors.date ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] hover:bg-[#070C0F] hover:text-[#ADADAD] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] hover:bg-white hover:text-[#595858] placeholder:text-[#595858]"}`}
                  >
                    {date ? date.toLocaleDateString() : "Select Date *"}
                    <ChevronDownIcon />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto overflow-hidden p-0 z-[10000]"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    captionLayout="dropdown"
                    fromYear={2025}
                    toYear={2026}
                    onSelect={(date) => {
                      setDate(date);
                      setOpen(false);
                      setData((prev) => ({
                        ...prev,
                        date: date
                          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                          : "",
                      }));
                      if (errors.date) {
                        setErrors((prev) => ({ ...prev, date: "" }));
                      }
                    }}
                    disabled={(date) => {
                      const isPublicHoliday = publicHolidays.some(
                        (d) =>
                          new Date(d).toDateString() === date.toDateString(),
                      );
                      const isDisabledDate = DisableDates.some(
                        (d) =>
                          new Date(d).toDateString() === date.toDateString(),
                      );
                      const isMonday = date.getDay() === 1;
                      const isFirstOfMonth = date.getDate() === 1;
                      const isToday =
                        date.toDateString() ===
                        new Date(todayDate).toDateString();

                      // Allow more days if current month is December
                      const today = new Date();
                      const currentMonth = today.getMonth(); // 0-11, where 11 is December
                      const daysToAdd = currentMonth === 11 ? 45 : 15;
                      const maxDate = new Date();
                      maxDate.setDate(today.getDate() + daysToAdd);

                      return (
                        isPublicHoliday ||
                        isDisabledDate ||
                        isMonday ||
                        isFirstOfMonth ||
                        isToday ||
                        date < new Date() ||
                        date > maxDate
                      );
                    }}
                  />
                </PopoverContent>
              </Popover>
              {errors.date && (
                <p className="text-red-500 text-xs mt-1 ml-1">{errors.date}</p>
              )}
            </div>

            <div className="mb-2">
              <label
                htmlFor={"select-time-slot-field" + `-${formfieldId}`}
                className="sr-only"
              >
                Select Time
              </label>
              <select
                className={`block !text-sm md:text-base w-full font-medium h-[48px] rounded-xl border py-3 px-4 !leading-none focus-visible:outline-none ${errors.slot ? "border-red-500" : darkBackground ? "text-[#ADADAD] bg-[#070C0F] border-[#070C0F] placeholder:text-[#ADADAD]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
                value={data?.slot || ""}
                id={"select-time-slot-field" + `-${formfieldId}`}
                name="slot"
                autoComplete="off"
                onChange={handleChange}
                title="Select Time"
              >
                <option disabled value="" className="text-black bg-[#e5e7eb]">
                  Select Time*
                </option>
                {slots?.map((slot) => {
                  return (
                    <option
                      key={uuidv4()}
                      value={slot}
                      className="text-black bg-[#e5e7eb]"
                    >
                      {slot}
                    </option>
                  );
                })}
              </select>
              {errors.slot && (
                <p className="text-red-500 text-xs mt-1 ml-1">{errors.slot}</p>
              )}
            </div>
            <button
              className="font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 w-full capitalize rounded-[24px_24px_24px_0px] mb-2"
              type="submit"
              disabled={formBtnText.disabled}
            >
              {formBtnText.disabled ? "Scheduling..." : "Schedule My Session"}
            </button>
            <p
              className={`font-medium md:text-[14px] text-[14px] text-center ${darkBackground ? "text-white" : "text-[#212953]"}`}
            >
              Not you?{" "}
              <span
                className={`cursor-pointer ${darkBackground ? "text-[#FFBA08]" : "text-[#bf8306]"}`}
                onClick={connectMetoDifferentAcount}
              >
                Connect using a different account
              </span>
            </p>
          </div>
        </form>
      )}

      {currentStep === 3 && getSuccessMessage(successMessage)}
    </div>
  );
};

export default GlobalForm;
